// AllRuns — /admin/runs. Cross-user run rollup, newest-first by
// started_at. Operator landing surface for "what's been running
// across the system" without per-user drill-in.
//
// Columns: run_id (click-through to RunTraceView), started_at,
// completed_at, status, total_tokens, cost. user_id + product_id
// rendered as secondary chips per row so the operator can pivot to
// the per-user views.
//
// Backend: GET /api/v1/admin/runs (admin_cross_user.go). In-memory
// aggregation; v1-scale.
//
// 0064: a saved pass (stopped on our side, waiting for the admin) reads
// PAUSED with its reason in the status cell, and a "Paused runs" table
// sits above the sample, from GET /api/v1/admin/runs/paused (one query,
// so the sample can never drop one), soonest closing first.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "../auth/useUser";
import { Header } from "../components/Header";
import { listAllRuns, listPausedRuns, type PausedRunRow } from "../api/traceApi";
import { Table, THead, TBody, TR, TH, TD } from "../components/Table";
import { pauseReasonWords } from "../lib/pauseWords";

function formatTs(ts?: string): string {
  if (!ts) return "—";
  // Drop the "Z" suffix and trim to second precision so the column
  // fits in the table without overflowing on wide UTC offsets.
  return ts.slice(0, 19).replace("T", " ");
}

function statusBadge(status: string): { label: string; className: string } {
  switch (status) {
    case "success":
      return { label: "OK", className: "text-emerald-300" };
    case "running":
      return { label: "RUNNING", className: "text-amber-300" };
    case "paused":
      return { label: "PAUSED", className: "text-orange-300" };
    case "failed_rolled_back":
      return { label: "FAIL", className: "text-rose-300" };
    case "failed_rollback_incomplete":
      return { label: "PARTIAL", className: "text-rose-400" };
    case "aborted":
    case "aborted_panic":
      return { label: status.toUpperCase(), className: "text-rose-400" };
    case "":
      return { label: "—", className: "text-ink-500" };
    default:
      return { label: status, className: "text-amber-300" };
  }
}

export function AllRuns() {
  const { data: me } = useUser();
  const q = useQuery({
    queryKey: ["admin", "all-runs"],
    queryFn: () => listAllRuns(100),
  });
  const pausedQ = useQuery({
    queryKey: ["admin", "paused-runs"],
    queryFn: listPausedRuns,
  });

  if (!me) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header user={me} />
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-10">
        <h1 className="text-display font-medium text-ink-50 tracking-tightest mb-2">
          All runs
        </h1>
        <p className="font-mono text-[11px] text-ink-400 mb-6">
          Recent runs across every user · newest first · sample of 100
        </p>

        {pausedQ.data && pausedQ.data.length > 0 && (
          <PausedRunsTable rows={pausedQ.data} />
        )}

        {q.isLoading && <p className="text-sm text-ink-400">loading…</p>}
        {q.error && (
          <p className="text-sm text-rose-300">
            Failed: {(q.error as Error).message}
          </p>
        )}

        {q.data && (
          <Table>
            <THead>
              <TH>run_id</TH>
              <TH>started</TH>
              <TH>ended</TH>
              <TH>status</TH>
              <TH>tokens</TH>
              <TH>cost</TH>
            </THead>
            <TBody>
              {q.data.map((r) => {
                const badge = statusBadge(r.status ?? "");
                const completed = r.completed_at;
                const isUnfinished =
                  !completed || completed.startsWith("0001-01-01");
                return (
                  <TR key={`${r.user_id}/${r.product_id}/${r.run_id}`}>
                    <TD>
                      {r.user_id && r.product_id && r.run_id ? (
                        <Link
                          to={`/users/${r.user_id}/products/${r.product_id}/runs/${r.run_id}`}
                          className="font-mono text-[11px] text-ink-100 hover:text-accent"
                        >
                          {r.run_id}
                        </Link>
                      ) : (
                        <span className="font-mono text-[11px] text-ink-300">
                          {r.run_id ?? "—"}
                        </span>
                      )}
                      <div className="text-[10px] text-ink-500 mt-0.5 font-mono">
                        {r.user_id ? (
                          <Link
                            to={`/users/${r.user_id}`}
                            className="hover:text-accent"
                          >
                            {r.user_id?.slice(0, 14)}…
                          </Link>
                        ) : "—"}
                        {" · "}
                        {r.user_id && r.product_id ? (
                          <Link
                            to={`/users/${r.user_id}/products/${r.product_id}`}
                            className="hover:text-accent"
                          >
                            {r.product_id?.slice(0, 14)}…
                          </Link>
                        ) : (r.product_id ?? "—")}
                      </div>
                    </TD>
                    <TD className="font-mono text-[11px] text-ink-300">
                      {formatTs(r.started_at)}
                    </TD>
                    <TD className="font-mono text-[11px] text-ink-300">
                      {isUnfinished ? "—" : formatTs(completed)}
                    </TD>
                    <TD>
                      <span
                        className={`font-mono text-[11px] font-bold ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                      {r.status === "paused" && r.pause_reason && (
                        <span className="font-mono text-[11px] text-orange-200">
                          {" · "}
                          {pauseReasonWords(r.pause_reason)}
                        </span>
                      )}
                    </TD>
                    <TD className="font-mono text-[11px] text-ink-300">
                      {(r.total_tokens ?? 0).toLocaleString()}
                    </TD>
                    <TD className="font-mono text-[11px] text-ink-300">
                      ${(r.cost_usd ?? 0).toFixed(3)}
                    </TD>
                  </TR>
                );
              })}
              {q.data.length === 0 && (
                <TR>
                  <TD>
                    <span className="text-ink-400">No runs recorded yet.</span>
                  </TD>
                </TR>
              )}
            </TBody>
          </Table>
        )}
      </main>
    </div>
  );
}

// PausedRunsTable lists every saved pass (0064), soonest closing first:
// they wait on the admin, so they come first on the page. The server
// answers them in that order; the table orders them so as well.
function PausedRunsTable({ rows }: { rows: PausedRunRow[] }) {
  const closes = (r: PausedRunRow) => {
    const t = Date.parse(r.closes_at ?? "");
    return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
  };
  const sorted = [...rows].sort((a, b) => closes(a) - closes(b));
  return (
    <section aria-label="Paused runs" className="mb-8">
      <h2 className="text-base font-medium text-orange-200 mb-2">Paused runs</h2>
      <Table>
        <THead>
          <TH>run</TH>
          <TH>reason</TH>
          <TH>stopped</TH>
          <TH>closes</TH>
          <TH>resets</TH>
        </THead>
        <TBody>
          {sorted.map((r) => (
            <TR key={`${r.user_id}/${r.product_id}/${r.run_id}`}>
              <TD>
                <Link
                  to={`/users/${r.user_id}/products/${r.product_id}/runs/${r.run_id}`}
                  className="font-mono text-[11px] text-ink-100 hover:text-accent"
                >
                  {r.run_id}
                </Link>
              </TD>
              <TD className="font-mono text-[11px] text-orange-200">
                {pauseReasonWords(r.reason)}
              </TD>
              <TD className="font-mono text-[11px] text-ink-300">
                {formatTs(r.stopped_at)}
              </TD>
              <TD className="font-mono text-[11px] text-ink-300">
                {formatTs(r.closes_at)}
              </TD>
              <TD className="font-mono text-[11px] text-ink-300">
                {r.resets_at ? formatTs(r.resets_at) : "—"}
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </section>
  );
}
